import React, { useEffect, useState } from "react";
import CommonTable from "../sharedComponents/CommonTable";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getClientFlowList,
  getClientsList,
  getCloneOfFlow,
  getDeleteFlow,
  getWritingAssistantFlowsList,
} from "../../service/ApiService";
import { AutoCompleteField } from "../sharedComponents/form-components/AutoCompleteField";
import Add from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { CommonDialog } from "../sharedComponents/CommonDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationPopup from "../sharedComponents/ConfirmationPopup";
import AlertMsg from "../sharedComponents/Alert";
import { FileCopy } from "@mui/icons-material";
import CloneDialog from "../sharedComponents/CloneDialog";

const FlowsView: React.FC = () => {
  const [clientFlowList, setClientFlowList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [WAFlowsList, setWAFlowsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedFlowToClone, setSelectedFlowToClone] = useState<any>(null);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false); // Separate state for dialog
  const [openCloneDialog, setOpenCloneDialog] = useState(false); // Separate state for dialog
  const [openConfirmation, setOpenConfirmation] = useState(false); // State for ConfirmationPopup
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertData, setAlertData] = useState<any>();
  const location = useLocation();
  const navigate = useNavigate();

  const columns: any = [
    {
      accessorKey: "flowId",
      header: "ID",
      size: 50,
    },
    {
      accessorKey: "flowName",
      header: "Name",
      size: 350,
    },
    {
      accessorKey: "traceName",
      header: "Trace",
      size: 200,
    },
  ];

  useEffect(() => {
    getClientsData();
    getWritingAssistantFlows();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedClient && Object.keys(selectedClient).length > 0) {
      params.set("clientCode", selectedClient.value);
    } else {
      params.delete("clientCode");
    }
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
    getFlowslist();
  }, [selectedClient]);

  const handleNavigateToAtion = (record: any) => {
    const featureId: any = new URLSearchParams(location.search).get(
      "featureId"
    );
    navigate(
      `/actionsView?featureId=${featureId}&flowId=${record.flowId}&clientCode=${selectedClient?.value}`
    );
  };

  const handleClickOpen = () => {
    setSelectedRowData({ clientCode: selectedClient?.value });
    setOpenDialog(true);
  };

  const handleClickClose = (isSubmitted: boolean) => {
    setOpenDialog(false);
    setSelectedRowData(null);
    if (isSubmitted) {
      getFlowslist();
    }
  };

  const handleCloneDialogOpen = () => {
    setOpenCloneDialog(true);
  };

  const handleCloneDialogClose = (isSubmitted: boolean) => {
    if (isSubmitted) {
      getCloneFlow();
    }
    else{
      setOpenCloneDialog(false);
      setSelectedFlowToClone(null);
    }
  };

  const getCloneFlow = () => {
    getCloneOfFlow(selectedFlowToClone.value, selectedClient.value)
      .then((response) => {
        if (response.success) {
          setOpenCloneDialog(false);
          setSelectedFlowToClone(null);
          setAlertData({ "type": true, "msg": `The flow has been cloned successfully` });
          getFlowslist();
        } else {
          setAlertData({ "type": false, "msg": response.errorMessage ?? `The flow clone was unsuccessful` });
        }
        setOpenSnackbar(true);
      })
      .catch((error: any) => {
        console.log('error', error)
      });
  }

  const getClientsData = () => {
    getClientsList().then((data: any) => {
      if (data && data.length > 0) {
        setClientsList(data);
        const selectedClientCode = new URLSearchParams(location.search).get(
          "clientCode"
        );
        if (selectedClientCode) {
          if (data && data.length > 0) {
            data.map((item: any) => {
              if (item.ClientCode === selectedClientCode) {
                let tempObj: any = {
                  value: item.ClientCode,
                  title: `${item.ClientCode} - ${item.CompanyName}`,
                };
                setSelectedClient(tempObj);
              }
            });
          }
        }
      }
    });
  };

  const getWritingAssistantFlows = () => {
    getWritingAssistantFlowsList().then((data: any) => {
      let tempList: any = [];
      if (data && data.length > 0) {
        data.map((item: any) => {
          if (item.id && item.name) {
            let tempObj: any = {
              value: item.id,
              title: item.name,
            };
            tempList.push(tempObj);
          }
        });
      }
      setWAFlowsList(tempList);
    });
  };

  const getFlowslist = () => {
    setIsLoading(true);
    const featureId: any = new URLSearchParams(location.search).get(
      "featureId"
    );

    getClientFlowList(featureId, selectedClient?.value)
      .then((response: any) => {
        setClientFlowList([]);
        if (response && response.length > 0) {
          const filteredResponse = response.filter((item: any) => !item.isDeleted);
          setClientFlowList(filteredResponse);
        }
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      })
      .catch((error: any) => {
        setIsLoading(false);
      });
  };

  const handleChange = (value: any) => {
    setSelectedClient(value);
  };

  const handleEditItem = (rowData: any, type: string) => {
    let tempObj: any = { ...rowData };
    tempObj.clientCode = tempObj.clientCode ?? selectedClient?.value;
    setSelectedRowData(tempObj);
    if (type === "edit") {
      setOpenDialog(true);
    } else if (type === "delete") {
      openConfirmationPop();
    }
  };

  const confirmationPopClose = () => {
    setOpenConfirmation(false);
  };

  const openConfirmationPop = () => {
    setOpenConfirmation(true);
  };

  const deleteFlow = () => {
    getDeleteFlow(selectedClient?.value, selectedRowData?.flowId)
      .then((response: any) => {
        setOpenSnackbar(true);
        if (response.success) {
          setAlertData({
            type: true,
            msg: `The flow has been deleted successfully`,
          });
          getFlowslist();
        } else {
          setAlertData({
            type: false,
            msg: response.errorMessage ?? `The flow deletion was unsuccessful`,
          });
        }
        confirmationPopClose();
      })
      .catch((error: any) => {
        console.log("error", error);
      });
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const renderCustomToolbar = (handleChange: any) => {
    const options: any = [];
    if (clientsList && clientsList.length > 0) {
      clientsList.map((item: any) => {
        if (item.ClientCode && item.CompanyName) {
          let tempObj: any = {
            value: item.ClientCode,
            title: `${item.ClientCode} - ${item.CompanyName}`,
          };
          options.push(tempObj);
        }
      });
    }

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Flows</Typography>
        <Box display="flex" gap={2}>
          <AutoCompleteField
            label="Client"
            itemsList={options}
            handleOnChange={handleChange}
            value={selectedClient}
          />
          <Tooltip
            title={
              selectedClient?.value
                ? ""
                : "Please select a client to add a new flow"
            }
          >
            <span>
              <Button
                disabled={!selectedClient?.value}
                variant="outlined"
                onClick={handleClickOpen}
              >
                <Add /> Add new flow
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              selectedClient?.value
                ? ""
                : "Please select a client to make clone of a flow"
            }
          >
            <span>
              <Button
                disabled={!selectedClient?.value}
                variant="outlined"
                onClick={handleCloneDialogOpen}
              >
                <FileCopy /> Clone a flow
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const renderRowActionMenuItems = (props: any) => {
    return [
      <MenuItem
        key={0}
        onClick={() => {
          handleNavigateToAtion(props.row.original);
          props.closeMenu();
        }}
        sx={{ m: 0 }}
      >
        <ListItemIcon>
          <VisibilityIcon />
        </ListItemIcon>
        Actions
      </MenuItem>,
      <MenuItem
        key={1}
        onClick={() => {
          handleEditItem(props.row.original, "edit");
          props.closeMenu();
        }}
        sx={{ m: 0 }}
      >
        <ListItemIcon>
          <EditIcon />
        </ListItemIcon>
        Edit
      </MenuItem>,
      <MenuItem
        key={2}
        onClick={() => {
          handleEditItem(props.row.original, 'delete');
          props.closeMenu();
        }}
        sx={{ m: 0 }}
      >
        <ListItemIcon>
          <DeleteIcon  />
        </ListItemIcon>
        Delete
      </MenuItem>,
    ];
  };

  return (
    <div className="container mx-auto">
      <CommonTable
        columnsList={columns}
        dataList={clientFlowList}
        renderCustomToolbar={() => renderCustomToolbar(handleChange)}
        isLoading={isLoading}
        renderRowActionMenuItems={renderRowActionMenuItems}
        enableRowActions={true}
        positionActionsColumn="last"
      />
      <CommonDialog
        isOpen={openDialog}
        title={`${selectedRowData?.flowId ? "Edit flow" : "Add new flow"}`}
        actionType="upsertFlow"
        dataToSet={selectedRowData}
        handleClose={handleClickClose}
      />
      <ConfirmationPopup
        isOpen={openConfirmation}
        description="Are you sure you want to delete the flow?"
        handleClose={confirmationPopClose}
        handleConfirm={deleteFlow}
      />
      <CloneDialog
        isOpen={openCloneDialog}
        data={WAFlowsList || []}
        handleClose={handleCloneDialogClose}
        selectedFlow={selectedFlowToClone}
        handleChangeFlow={setSelectedFlowToClone}
      />
      <AlertMsg
        openSnackbar={openSnackbar}
        alertdata={alertData}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default FlowsView;
